/**
 * Import Monitor — ERP Received.
 *
 * CR-7468. What the order feed said it had received on a line, against what the
 * receipts hold. Platform operators only; the server enforces it.
 */
import api from './client';

export type ErpReceivedDirection = 'erp_higher' | 'helix_higher' | 'agree';

export interface ErpReceivedRow {
  po_line_id: number;
  po_number: string;
  line_number: number;
  part_number: string | null;
  supplier_name: string | null;
  status: string;
  ordered_qty: string;
  erp_received_qty: string;
  gross_received: string;
  /** Null when the backend has no derived figures for the line at all.
   *
   *  NOT the same as `no_receipts`, and do not switch one for the other: a line
   *  with no receipts but a counted NCR against it IS derived (accepted 0), so it
   *  arrives here as "0" with `no_receipts` true. Dispatch on `no_receipts`. */
  accepted_qty: string | null;
  stored_received_qty: string;
  /** ERP figure less the receipts' gross. Positive = the ERP booked more. */
  difference: string;
  erp_reported_at: string | null;
  /**
   * No receipt held in Helix for this line, so there is nothing to compare the
   * feed against. A different thing from a disagreement, and the reason the tab
   * separates them: on one tenant this is 26,587 lines whose deliveries predate
   * the receipt history we hold (R-609).
   */
  no_receipts: boolean;
}

export interface ErpReceivedResponse {
  summary: { total: number; erp_higher: number; helix_higher: number; agree: number };
  count: number;
  page: number;
  page_size: number;
  results: ErpReceivedRow[];
}

export interface ErpReceivedFilters {
  search?: string;
  direction?: ErpReceivedDirection;
}

function toParams(customerId: number, filters: ErpReceivedFilters): Record<string, string | number> {
  const params: Record<string, string | number> = { customer_id: customerId };
  if (filters.search) params['search'] = filters.search;
  if (filters.direction) params['direction'] = filters.direction;
  return params;
}

export const erpReceivedAPI = {
  /** One page of compared lines plus summary counts. */
  list: (
    customerId: number,
    filters: ErpReceivedFilters,
    page: number,
    pageSize: number,
  ): Promise<ErpReceivedResponse> =>
    api.get<ErpReceivedResponse>('/imports/erp-received/', {
      params: { ...toParams(customerId, filters), page, page_size: pageSize },
    }).then(r => r.data),

  /** Every row matching the filters, as an .xlsx blob. */
  exportXlsx: (customerId: number, filters: ErpReceivedFilters): Promise<Blob> =>
    api.get('/imports/erp-received/export/', {
      params: toParams(customerId, filters),
      responseType: 'blob',
    }).then(r => r.data as Blob),
};
